import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormGenerator } from '../helper/FormGenerator';
import { PageList, FormLayout } from '../components/PageComponents';
import { usePageAPI } from '../hooks/usePageAPI';
import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import { idFormat } from '../utils/appUtils';
import { useTokens } from '../context/TokenContext';

const AdminTags = () => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { fetchPages, fetchPage, createPage, updatePage, deletePage } = usePageAPI();
  const [formData, setFormData] = useState({ name: '' });
  const [pageData, setPageData] = useState([]);
  const [brandData, setTagData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('title');
  const [searchValue, setSearchValue] = useState('');
  const [isReload, setIsReload] = useState(false);

  const pageType = {
    isNew: pathname.includes('new'),
    isEdit: pathname.includes('edit'),
    isList: !(pathname.includes('new') || pathname.includes('edit')),
  };

  const formFields = [
    { id: "name", label: "Tag Name", type: "text", placeholder: "Enter Tag Name", required: true },
  ];

  useEffect(() => {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'title';
    const searchValue = params.get('searchValue') || '';

    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    if (pageType.isList) {
      fetchPages({
        type: 'tags',
        limit: limit,
        currentPage: page,
        searchBy: searchBy,
        searchValue: searchValue,
        setLoading: setLoading,
        setProgress: setProgress
      }).then(({ data, dataCount }) => {
        setPageData({
          data,
          dataCount,
        });
      });
    }

    if (pageType.isEdit) {
      const pageId = pathname.split('/').pop();
      fetchPage({ type: 'tags', pageId: pageId, setLoading: setLoading, setProgress: setProgress }).then(data => {
        setFormData({
          name: data?.data?.data?.name || '',
        });
      });
    }
    
    if (isReload) {
      setIsReload(false);
    }
  }, [pathname, search, isReload]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formDataToSubmit = new FormData();
    formDataToSubmit.append('name', formData.name);

    const toastId = toast.loading(`Page ${pageType.isEdit ? 'updating ...' : 'creating ...'}`, { autoClose: false });

    try {
      const pageId = pageType.isEdit ? pathname.split('/').pop() : null;

      const result = pageType.isEdit
        ? await updatePage({ type: 'tags', pageId: pageId, formDataToSubmit: formDataToSubmit })
        : await createPage({ type: 'tags/', formDataToSubmit: formDataToSubmit });


      if (result.data.success == true) {
        toast.update(toastId, {
          render: `Tag ${pageType.isEdit ? 'updated' : 'created'} successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });

        navigate(`/admin/tag/edit/${pageType.isEdit ? pageId : result.data.data.id}`);
      } else {
        toast.update(toastId, {
          render: result.data.message || 'An unknown error occurred',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      const errorMessage = error?.result?.data?.message || 'An unknown error occurred';

      // Update toast with error message
      toast.update(toastId, {
        render: errorMessage, 
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      console.error('Error occurred:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this page?")) {
      const toastId = toast.loading('Deleting ...', { autoClose: false });
      const success = await deletePage({ type: 'tags', id: id, setLoading: setLoading });
      if (success) {
        setPageData(prev => ({
          ...prev,
          data: prev.data.filter(page => page.id !== id),
        }));
        toast.update(toastId, {
          render: `Page deleted successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/tag/list', search: params.toString() });
  };

  return (
    <>
      <Head title={getPageTitle(pageType)} />

      {pageType.isNew || pageType.isEdit ? (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <FormLayout
            title={pageType.isEdit ? "Edit Tag" : "Create New Tag"}
            submitText={pageType.isEdit ? (loading ? 'Updating ...' : 'Update') : (loading ? 'Creating ...' : 'Create')}
            onCancel={() => navigate('/admin/tag')}
            onSubmit={handleFormSubmit}
            loading={loading}
          >
            <FormGenerator
              fields={formFields}
              fieldData={formData}
              onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
              authToken={adminAuthToken}
            />
            <FormGenerator
              fields={[]}
              fieldData={formData}
              onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
              authToken={adminAuthToken}
            />
          </FormLayout>
      ) : null}

      {pageType.isList && (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <PageList
            itemType={'tags'}
            itemId={'id'}
            pageTitle={'Tag List'}
            pageData={pageData.data}
            actionApplyKeyMap={{
              'Delete': 'delete',
            }}
            columns={['Id', 'Tag Name']}
            filterBy={{ '--select--': '', 'Tag Id': 'id', 'Tag Name': 'name' }}
            columnKeyMap={{
              'Id': item => idFormat(item.id),
              'Tag Name': (item) => (
                <Link className='a_edit ms-2' to={`/admin/tag/edit/${item.id}`}>{item.name}</Link>
              )
            }}
            onAddNew={() => navigate('/admin/tag/new')}
            onView={(id) => navigate(`/admin/tag/view/${id}`)}
            onEdit={(id) => navigate(`/admin/tag/edit/${id}`)}
            onDelete={handlePageDelete}
            totalRows={pageData.dataCount}
            updateUrlParams={updateUrlParams}
            isReload={setIsReload}
          />
      )}
    </>
  );
};

const getPageTitle = ({ isEdit, isNew }) =>
  isEdit ? "Edit Tag" : isNew ? "Create New Tag" : "Page List";

export default AdminTags;
